"use client";

import { useCallback, useMemo } from "react";
import { useRouter } from "next/navigation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getAllInstalledIntegrations,
  InstalledIntegration_ALL_GET,
} from "@/api/client/installedintegration";
import { InstalledIntegrationStatus } from "@/types/installedintegration";
import { Alert, AlertAction, AlertDescription, Icon } from "@focal/ui-kit";
import { useWebSocketSubscription } from "@/context/WebSocketProvider";
import { useReconnectRefetch } from "@/hooks/useReconnectRefetch";
import { CloudEventType } from "@/types/cloud-events";
import { routes } from "@/constants";
import { RECONNECT_DISPLAY_NAMES } from "./supportedIntegrations";

const buildText = (displayNames: string[]) => {
  if (displayNames.length === 1) {
    return `Your ${displayNames[0]} connection needs to be reconnected.`;
  }
  const last = displayNames[displayNames.length - 1];
  const head = displayNames.slice(0, -1).join(", ");
  return `Your ${head} and ${last} connections need to be reconnected.`;
};

export const IntegrationReconnectBanner = () => {
  const router = useRouter();
  const queryClient = useQueryClient();

  // Credential-health changes (NEEDS_RECONNECT / restored to ACTIVE) arrive on
  // the per-user `integration` room as INTEGRATION_STATUS_CHANGED; invalidate
  // the shared installed-integrations cache so the banner appears/disappears
  // without polling.
  const handleIntegrationMessage = useCallback(
    (event: { type: string }) => {
      if (event.type === CloudEventType.INTEGRATION_STATUS_CHANGED) {
        queryClient.invalidateQueries({
          queryKey: [InstalledIntegration_ALL_GET],
          exact: true,
        });
      }
    },
    [queryClient],
  );
  useWebSocketSubscription("integration", undefined, handleIntegrationMessage);

  // Events are dropped while the socket is down; reconcile on reconnect.
  useReconnectRefetch(() => {
    queryClient.invalidateQueries({
      queryKey: [InstalledIntegration_ALL_GET],
      exact: true,
    });
  });

  const { data: integrations } = useQuery({
    queryFn: getAllInstalledIntegrations,
    queryKey: [InstalledIntegration_ALL_GET],
    staleTime: 5 * 60 * 1000,
  });

  const displayNames = useMemo(() => {
    if (!integrations) return [];
    const matched = new Set<string>();
    for (const row of integrations) {
      if (row.status !== InstalledIntegrationStatus.NEEDS_RECONNECT) continue;
      const displayName = RECONNECT_DISPLAY_NAMES[row.integration];
      if (displayName) matched.add(displayName);
    }
    return Array.from(matched);
  }, [integrations]);

  if (displayNames.length === 0) {
    return null;
  }

  return (
    <div
      className="relative z-10 px-2 py-1"
      data-cy="integration-reconnect-banner"
    >
      <Alert
        variant="warning"
        icon={<Icon name="WarningIcon" />}
        action={
          <AlertAction
            onClick={() => router.push(routes.settings.integrations)}
            data-cy="integration-reconnect-cta"
          >
            Reconnect
          </AlertAction>
        }
        data-cy="integration-reconnect"
      >
        <AlertDescription
          className="mt-0 flex min-h-7 items-center"
          data-cy="integration-reconnect-text"
        >
          {buildText(displayNames)}
        </AlertDescription>
      </Alert>
    </div>
  );
};
